import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  TextField, 
  InputAdornment,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Chip,
  Button,
  Autocomplete,
  Pagination,
  PaginationItem,
  Stack,
  IconButton,
  Card,
  CardActionArea,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterListIcon, Clear as ClearIcon } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GavelIcon from '@mui/icons-material/Gavel';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BalanceIcon from '@mui/icons-material/Balance';
import DocumentCard from '../components/DocumentCard';
import AuthContext, { AuthContextType } from '../contexts/AuthContext';
import axios from 'axios';
import API from '../utils/http';
import { useOptimizedSearch, measurePerformance } from '../utils/searchOptimization';
import { trackSearch, trackAddToFavorites, trackRemoveFromFavorites, trackDocumentView } from '../utils/analytics';
import { useDocumentFilters } from '../hooks/useDocumentFilters';

interface Document {
  id: string;
  title: string;
  description?: string;
  fileUrl: string;
  fileSize: number;
  uploadDate: string;
  viewCount: number;
  downloadCount: number;
  favoriteCount: number;
  keywords?: string[];
  cities?: string[];
  userId?: string;
  content?: string;
}

interface PaginatedResponse {
  documents: Document[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isAuthenticated } = useContext<AuthContextType>(AuthContext);
  
  const [documents, setDocuments] = useState<Document[]>([]);
  const [favorites, setFavorites] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [userFavorites, setUserFavorites] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);

  const {
    searchTerm,
    inputValue,
    setSearchTerm,
    setInputValue,
    selectedCities,
    setSelectedCities,
    availableCities,
    numeroSentenza,
    setNumeroSentenza,
    numeroSentenzaInput,
    setNumeroSentenzaInput,
    showFilters,
    setShowFilters,
    currentPage,
    setCurrentPage,
    hasActiveFilters,
    handleInputChange,
    handleNumeroSentenzaInputChange,
    applyFilters,
    handleCitiesChange,
    removeCity,
    clearSearchTerm,
    clearNumeroSentenza,
    clearAllFilters,
  } = useDocumentFilters(token);

  // Carica i documenti quando cambia il termine di ricerca, le città selezionate, il numero sentenza o la pagina
  useEffect(() => {
    console.log("[UserDashboard] useEffect per fetchDocuments attivato", {
      currentPage,
      activeTab,
      searchTermLength: searchTerm?.length || 0,
      selectedCitiesCount: selectedCities?.length || 0,
      numeroSentenza
    });
    fetchDocuments();
  }, [token, searchTerm, selectedCities, numeroSentenza, currentPage, activeTab]);

  // Ripristina lo stato della ricerca quando si torna dal viewer
  useEffect(() => {
    let searchState = (location.state as any)?.searchState;
    
    // Se non c'è stato nel location, prova a recuperarlo da sessionStorage
    if (!searchState) {
      const savedState = sessionStorage.getItem('dashboardSearchState');
      if (savedState) {
        try {
          searchState = JSON.parse(savedState);
          console.log('[UserDashboard] Ripristino stato da sessionStorage:', searchState);
        } catch (e) {
          console.error('Errore nel parsing dello stato salvato:', e);
        }
      }
    }
    
    if (searchState) {
      console.log('[UserDashboard] Ripristino stato ricerca:', searchState);
      
      setSearchTerm(searchState.searchTerm || '');
      setInputValue(searchState.searchTerm || '');
      setSelectedCities(searchState.cities || []);
      setNumeroSentenza(searchState.numeroSentenza || '');
      setNumeroSentenzaInput(searchState.numeroSentenza || '');
      setCurrentPage(searchState.page || 1);
      setActiveTab(searchState.activeTab || 0);
      
      // Pulisci lo state per evitare loop
      window.history.replaceState({}, document.title);
      
      // Pulisci sessionStorage dopo il ripristino
      sessionStorage.removeItem('dashboardSearchState');
    }
  }, [location.state]);

  // Tiene traccia dell'ultima richiesta avviata: se una risposta più vecchia arriva dopo
  // (fuori ordine, es. subito dopo il ripristino filtri al rientro dal viewer), viene ignorata
  // invece di sovrascrivere risultati più recenti e corretti con dati ormai obsoleti.
  const fetchRequestId = useRef(0);

  // 🚀 Fetch ottimizzato con cache e performance monitoring
  const fetchDocuments = measurePerformance('fetchDocuments', async () => {
    const requestId = ++fetchRequestId.current;
    try {
      setLoading(true);
      setError(null);
      
      // Costruisci i parametri di richiesta
      const params: Record<string, any> = {};
      
      if (searchTerm) {
        params.searchTerm = searchTerm;
      }
      
      if (selectedCities.length > 0) {
        params.cities = selectedCities;
      }

      if (numeroSentenza) {
        params.numeroSentenza = numeroSentenza;
      }

      // Configura l'autenticazione
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      if (activeTab === 0) {
        // Fetch documenti con filtri usando la nuova API utilità
        console.log(`[UserDashboard] Richiesta documenti con paginazione:`, {
          currentPage,
          pageSize: 10,
          params
        });
        
        const response = await API.getPaginated<PaginatedResponse>(
          '/api/documents',
          currentPage,
          10,
          params,
          config
        );
        
        console.log('[UserDashboard] Risposta documenti del server:', {
          documents: response.data.documents?.length || 0,
          page: response.data.page,
          totalPages: response.data.totalPages,
          total: response.data.total
        });

        // Ignora questa risposta se nel frattempo è già partita una richiesta più recente
        if (requestId !== fetchRequestId.current) return;

        setDocuments(response.data.documents || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocuments(response.data.total || 0);

        // 📊 Traccia ricerca su Google Analytics (solo se c'è un termine di ricerca)
        if (searchTerm) {
          trackSearch(searchTerm, {
            cities: selectedCities,
            resultsCount: response.data.total || 0,
          });
        }
      } else if (activeTab === 1) {
        // Fetch preferiti usando la nuova API utilità
        console.log(`[UserDashboard] Richiesta preferiti con paginazione:`, {
          currentPage,
          pageSize: 10,
          params
        });
        
        const favResponse = await API.getPaginated<PaginatedResponse>(
          '/api/documents/favorites',
          currentPage,
          10,
          params,
          config
        );
        
        console.log('[UserDashboard] Risposta preferiti del server:', {
          documents: favResponse.data.documents?.length || 0,
          page: favResponse.data.page,
          totalPages: favResponse.data.totalPages,
          total: favResponse.data.total
        });

        // Ignora questa risposta se nel frattempo è già partita una richiesta più recente
        if (requestId !== fetchRequestId.current) return;

        setFavorites(favResponse.data.documents || []);
        setTotalPages(favResponse.data.totalPages || 1);
        setTotalDocuments(favResponse.data.total || 0);
        
        // Aggiorna l'elenco degli ID dei documenti preferiti
        setUserFavorites((favResponse.data.documents || []).map((doc: Document) => doc.id));
      }
    } catch (err: any) {
      console.error('Errore nel recupero dei documenti:', err);
      if (requestId === fetchRequestId.current) {
        setError(err.response?.data?.message || 'Errore nel caricamento dei documenti.');
      }
    } finally {
      if (requestId === fetchRequestId.current) {
        setLoading(false);
      }
    }
  });

  // Ricerca ottimizzata con debounce (300ms) - combina testo libero e numero sentenza
  const optimizedSearch = useOptimizedSearch(applyFilters, 300);

  const handleSearch = () => {
    optimizedSearch();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    console.log(`[UserDashboard] handlePageChange - Cambio pagina da ${currentPage} a ${value}`);
    setCurrentPage(value);
  };

  const toggleFavorite = async (documentId: string) => {
    try {
      const isFavorite = userFavorites.includes(documentId);
      
      if (isFavorite) {
        // Rimuovi dai preferiti
        await axios.delete(`/api/documents/favorites/${documentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setFavorites(favorites.filter(doc => doc.id !== documentId));
        setUserFavorites(userFavorites.filter(id => id !== documentId));
        
        // 📊 Traccia rimozione preferiti su GA4
        trackRemoveFromFavorites(documentId);
      } else {
        // Aggiungi ai preferiti
        await axios.post(
          '/api/documents/favorites',
          { documentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Aggiungi alla lista dei preferiti
        const docToAdd = documents.find(doc => doc.id === documentId);
        if (docToAdd) {
          setFavorites([...favorites, docToAdd]);
          setUserFavorites([...userFavorites, documentId]);
          
          // 📊 Traccia aggiunta preferiti su GA4
          trackAddToFavorites(documentId, docToAdd.title);
        }
      }
    } catch (err) {
      console.error('Errore nella gestione dei preferiti:', err);
    }
  };

  const handleViewDocument = (id: string) => {
    // 📊 Traccia visualizzazione documento su GA4
    const doc = [...documents, ...favorites].find(d => d.id === id);
    if (doc) {
      trackDocumentView(id, doc.title);
    }
    
    // Salva lo stato della ricerca corrente
    const searchState = {
      searchTerm: searchTerm,
      cities: selectedCities,
      numeroSentenza: numeroSentenza,
      page: currentPage,
      activeTab: activeTab
    };
    
    // Salva anche in sessionStorage per gestire il back del browser
    sessionStorage.setItem('dashboardSearchState', JSON.stringify(searchState));
    
    navigate(`/viewer/${id}`, { 
      state: { 
        returnTo: '/dashboard', 
        searchState: searchState 
      } 
    });
  };

  // Filtra per tab attivo (tutti i documenti o preferiti)
  const displayedDocuments = activeTab === 0 ? documents : favorites;

  const widgets = [
    {
      label: 'BOOKY SEARCH',
      desc: 'Ricerca AI in linguaggio naturale',
      icon: <AutoAwesomeIcon sx={{ fontSize: 32 }} />,
      to: '/booky-search',
      nameColor: '#a3e635',
      descColor: 'rgba(163,230,53,0.7)',
      iconColor: '#a3e635',
      bg: 'linear-gradient(145deg, #0d1f0a, #1a3510)',
      border: 'rgba(132,204,22,0.28)',
      shadow: 'rgba(132,204,22,0.18)',
    },
    {
      label: 'BOOKY TRIBUNALE',
      desc: 'Orientamenti per sede giudiziaria',
      icon: <GavelIcon sx={{ fontSize: 32 }} />,
      to: '/booky-tribunale',
      nameColor: '#c084fc',
      descColor: 'rgba(192,132,252,0.7)',
      iconColor: '#c084fc',
      bg: 'linear-gradient(145deg, #160a24, #251040)',
      border: 'rgba(168,85,247,0.28)',
      shadow: 'rgba(168,85,247,0.18)',
    },
    // {
    //   label: 'BOOKY WORK',
    //   desc: 'Fascicoli e AI per il tuo caso',
    //   icon: <WorkspacePremiumIcon sx={{ fontSize: 32 }} />,
    //   to: '/booky-work',
    //   nameColor: '#60a5fa',
    //   descColor: 'rgba(96,165,250,0.7)',
    //   iconColor: '#60a5fa',
    //   bg: 'linear-gradient(145deg, #071524, #0d2245)',
    //   border: 'rgba(37,99,235,0.28)',
    //   shadow: 'rgba(37,99,235,0.18)',
    // },
    // {
    //   label: 'BOOKY GIUDICE',
    //   desc: 'Interroga il profilo del giudice',
    //   icon: <BalanceIcon sx={{ fontSize: 32 }} />,
    //   to: '/booky-giudice',
    //   nameColor: '#fbbf24',
    //   descColor: 'rgba(251,191,36,0.7)',
    //   iconColor: '#fbbf24',
    //   bg: 'linear-gradient(145deg, #1a0f02, #2e1c06)',
    //   border: 'rgba(217,119,6,0.28)',
    //   shadow: 'rgba(217,119,6,0.18)',
    // },
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', px: { xs: 2, md: 3 } }}>

      {/* ── 80% SINISTRA ── */}
      <Box sx={{ flex: '0 0 80%', maxWidth: '80%', minWidth: 0 }}>
    <Box sx={{ py: 3 }} className="fade-in">
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          className="page-title"
          sx={{ 
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            mb: 3
          }}
        >
          Archivio di Giurisprudenza di Merito
        </Typography>
        {user && (
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 4,
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              lineHeight: 1.7,
              maxWidth: '800px'
            }}
            className="slide-up"
          >
            Benvenuto, <Box component="span" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>{user.name}</Box>. 
            Consulta, ricerca e organizza provvedimenti di merito provenienti dai tribunali italiani. Trova rapidamente decisioni rilevanti per ufficio giudiziario, parole chiave e contenuto.
          </Typography>
        )}
        
        <Box 
          className="glass-card enhanced-search-container"
          sx={{ 
            p: 0.8,
            mb: 4,
            borderRadius: 'var(--border-radius-xl)',
            display: 'flex',
            alignItems: 'center',
            backdropFilter: 'blur(10px)',
            background: 'var(--glass-background)',
            border: 'var(--glass-border)',
          }}
        >
          <TextField
            fullWidth
            placeholder="Cerca per titolo, contenuto, città o parole chiave..."
            variant="outlined"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--primary-color)' }} />
                </InputAdornment>
              ),
              endAdornment: inputValue && (
                <InputAdornment position="end">
                  <IconButton 
                    edge="end" 
                    onClick={() => setInputValue('')}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { 
                borderRadius: '30px',
                '& fieldset': { border: 'none' },
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '0.95rem',
                py: 0.5
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ 
              height: '40px',
              minWidth: '80px',
              borderRadius: '20px',
              ml: 1,
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          >
            Cerca
          </Button>
        </Box>
        
        <Button 
          startIcon={<FilterListIcon />} 
          onClick={() => setShowFilters(!showFilters)}
          className="modern-button"
          variant="contained"
          color="primary"
          size="medium"
          sx={{ 
            mb: 1.5,
            borderRadius: 'var(--border-radius-md)',
            textTransform: 'none',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 600,
            bgcolor: 'var(--secondary-color)',
            color: '#fff',
            boxShadow: 'var(--shadow-md)',
            '&:hover': {
              bgcolor: 'var(--primary-color)',
              boxShadow: 'var(--shadow-lg)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            },
            px: 3,
            py: 1
          }}
        >
          {showFilters ? 'Nascondi filtri' : 'Filtri avanzati'}
        </Button>
        
        {showFilters && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 'var(--border-radius-lg)',
              border: '1px solid var(--border-light)',
              background: 'linear-gradient(145deg, var(--card-background), rgba(255,255,255,0.8))'
            }}
            className="fade-blur-in"
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2,
                fontFamily: 'Syne, sans-serif',
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
            >
              Filtra per città
            </Typography>
            <Autocomplete
              multiple
              id="cities-filter"
              options={availableCities}
              value={selectedCities}
              onChange={(_, newValue) => handleCitiesChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Seleziona una o più città..."
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 'var(--border-radius-md)',
                      fontFamily: 'Plus Jakarta Sans, sans-serif'
                    }
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    color="primary"
                    variant="filled"
                    sx={{
                      borderRadius: '20px',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      bgcolor: 'var(--secondary-color)',
                      fontWeight: 500
                    }}
                  />
                ))
              }
            />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    fontFamily: 'Syne, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}
                >
                  Filtra per numero sentenza
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Es. 170/2026 oppure 170 2026"
                  fullWidth
                  value={numeroSentenzaInput}
                  onChange={handleNumeroSentenzaInputChange}
                  onKeyDown={handleKeyDown}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 'var(--border-radius-md)',
                      fontFamily: 'Plus Jakarta Sans, sans-serif'
                    }
                  }}
                  InputProps={{
                    endAdornment: numeroSentenzaInput && (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={clearNumeroSentenza}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>
      
      <Box sx={{ mb: 2 }}>
                  <Paper 
            className="enhanced-tabs"
            sx={{ 
              borderRadius: 'var(--border-radius-lg)',
              overflow: 'hidden',
              border: '1px solid var(--border-light)',
              boxShadow: 'var(--shadow-sm)'
            }}
            elevation={0}
          >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                py: 1.8
              }
            }}
          >
            <Tab label="Tutti i Documenti" />
            <Tab label="I Miei Preferiti" />
          </Tabs>
        </Paper>
      </Box>

      {/* Mostra i filtri attivi */}
      {hasActiveFilters && (
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: 'var(--border-radius-lg)',
            bgcolor: 'rgba(195, 143, 255, 0.05)',
            border: '1px dashed rgba(195, 143, 255, 0.3)'
          }}
          className="fade-in"
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Filtri attivi:
          </Typography>

          {searchTerm && (
            <Chip
              label={`Ricerca: ${searchTerm}`}
              onDelete={clearSearchTerm}
              color="primary"
              sx={{
                borderRadius: '20px',
                bgcolor: 'var(--secondary-color)',
                fontFamily: 'Plus Jakarta Sans, sans-serif'
              }}
            />
          )}

          {selectedCities.map((city: string) => (
            <Chip
              key={city}
              label={`Città: ${city}`}
              onDelete={() => removeCity(city)}
              sx={{
                borderRadius: '20px',
                bgcolor: 'rgba(56, 189, 248, 0.2)',
                color: 'var(--primary-color)',
                fontFamily: 'Plus Jakarta Sans, sans-serif'
              }}
            />
          ))}

          {numeroSentenza && (
            <Chip
              label={`N. sentenza: ${numeroSentenza}`}
              onDelete={clearNumeroSentenza}
              sx={{
                borderRadius: '20px',
                bgcolor: 'rgba(56, 189, 248, 0.2)',
                color: 'var(--primary-color)',
                fontFamily: 'Plus Jakarta Sans, sans-serif'
              }}
            />
          )}

          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={clearAllFilters}
              sx={{
                ml: 'auto',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                textTransform: 'none',
                fontWeight: 600,
                color: 'var(--primary-color)'
              }}
            >
              Cancella tutti
            </Button>
          )}
        </Box>
      )}

      {loading ? (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flexDirection: 'column',
            py: 10,
            gap: 2
          }}
        >
          <CircularProgress 
            color="primary"
            size={50}
            thickness={4}
            sx={{ color: 'var(--secondary-color)' }}
          />
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.6 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.6 }
              }
            }}
          >
            Caricamento documenti...
          </Typography>
        </Box>
      ) : error ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            px: 3,
            borderRadius: 'var(--border-radius-lg)',
            bgcolor: 'rgba(244, 63, 94, 0.05)',
            border: '1px solid rgba(244, 63, 94, 0.1)',
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          <Typography variant="h6" color="error" gutterBottom sx={{ fontFamily: 'Syne, sans-serif', fontWeight: 600 }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ 
              mt: 2,
              borderRadius: 'var(--border-radius-md)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'var(--shadow-md)'
            }}
            onClick={() => window.location.reload()}
          >
            Riprova
          </Button>
        </Box>
      ) : displayedDocuments.length === 0 ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            px: 3,
            borderRadius: 'var(--border-radius-lg)',
            bgcolor: 'rgba(56, 189, 248, 0.05)',
            border: '1px solid rgba(56, 189, 248, 0.1)',
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          <Typography 
            variant="h6" 
            color="text.secondary" 
            gutterBottom 
            sx={{ 
              fontFamily: 'Syne, sans-serif', 
              fontWeight: 600,
              mb: 2
            }}
          >
            {searchTerm || selectedCities.length > 0
              ? 'Nessun documento trovato con i filtri selezionati' 
              : activeTab === 0 
                ? 'Nessun documento disponibile' 
                : 'Nessun documento nei preferiti'}
          </Typography>
          {(searchTerm || selectedCities.length > 0) && (
            <Button 
              variant="outlined" 
              sx={{ 
                mt: 2,
                borderRadius: 'var(--border-radius-md)',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'var(--info-color)',
                color: 'var(--info-color)',
                '&:hover': {
                  borderColor: 'var(--info-color)',
                  bgcolor: 'rgba(56, 189, 248, 0.05)'
                }
              }}
              onClick={clearAllFilters}
            >
              Cancella filtri
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            <Grid container spacing={3} className="slide-up">
              {displayedDocuments.map((doc, index) => (
                <Grid 
                  item 
                  xs={12}
                  key={doc.id}
                  className="stagger-item"
                  sx={{ 
                    animation: `slideUp 0.5s cubic-bezier(0.33, 1, 0.68, 1) forwards ${index * 0.08}s`,
                    opacity: 0 
                  }}
                >
                  <DocumentCard 
                    document={doc}
                    isFavorite={userFavorites.includes(doc.id)}
                    onToggleFavorite={() => toggleFavorite(doc.id)}
                    onViewDocument={handleViewDocument}
                  />
                </Grid>
              ))}
            </Grid>
            
            {/* Paginazione */}
            {totalPages > 1 && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  mt: 6, 
                  mb: 2,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '150px',
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, var(--border-light), transparent)'
                  }
                }}
                className="fade-in"
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  renderItem={(item) =>
                    (item.type === 'previous' || item.type === 'next')
                      ? <PaginationItem {...item} />
                      : null
                  }
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontWeight: 500,
                      borderRadius: 'var(--border-radius-md)',
                      transition: 'all 0.2s cubic-bezier(0.33, 1, 0.68, 1)'
                    },
                    '& .Mui-selected': {
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, var(--primary-color), var(--primary-light))',
                      boxShadow: 'var(--shadow-sm)'
                    },
                    '& .MuiPaginationItem-root:hover': {
                      background: 'rgba(195, 143, 255, 0.1)'
                    }
                  }}
                />
                {/* <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  align="center"
                  sx={{ 
                    mt: 2,
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '0.9rem'
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                    {((currentPage - 1) * 10) + 1}-{Math.min(currentPage * 10, totalDocuments)}
                  </Box>{' '}
                  di {totalDocuments} documenti
                </Typography> */}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
      </Box>

      {/* ── 20% DESTRA — widget sticky ── */}
      <Box sx={{
        width: { xs: 0, lg: '20%' },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        gap: 1.5,
        pt: 3,
        pl: 2.5,
        pr: 1.5,
        flexShrink: 0,
        position: 'sticky',
        top: 72,
        alignSelf: 'flex-start',
      }}>
        <Typography sx={{
          fontSize: '9px', fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: 'rgba(100,116,139,0.7)', mb: 0.5, pl: 0.5,
        }}>
          Strumenti AI
        </Typography>

        {widgets.map(w => (
          <Card
            key={w.label}
            elevation={0}
            sx={{
              border: `1.5px solid ${w.border}`,
              borderRadius: '14px',
              background: w.bg,
              overflow: 'hidden',
              transition: 'transform 0.2s cubic-bezier(.22,.68,0,1.2), box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-5px) scale(1.01)',
                boxShadow: `0 12px 32px ${w.shadow}`,
                border: `1.5px solid ${w.border.replace('0.22', '0.55')}`,
              },
            }}
          >
            <CardActionArea
              component={Link}
              to={w.to}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                minHeight: 170,
                position: 'relative',
                /* glow spot in top-right */
                '&::before': {
                  content: '""',
                  position: 'absolute', top: 0, right: 0,
                  width: '70px', height: '70px',
                  background: `radial-gradient(circle at top right, ${w.border.replace('0.22', '0.18')}, transparent 70%)`,
                  pointerEvents: 'none',
                },
                '&::after': {
                  content: '"→"',
                  position: 'absolute', bottom: 14, right: 14,
                  fontSize: 14, color: w.nameColor, opacity: 0.3,
                  transition: 'opacity 0.15s, transform 0.15s',
                },
                '&:hover::after': { opacity: 1, transform: 'translateX(4px)' },
              }}
            >
              <Box sx={{
                color: w.iconColor,
                background: `${w.border.replace('0.28', '0.14')}`,
                borderRadius: '10px',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {w.icon}
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: 'Syne, sans-serif', fontWeight: 800,
                  fontSize: '15px', color: w.nameColor, lineHeight: 1.25, mb: 0.6,
                  letterSpacing: '0.03em',
                }}>
                  {w.label}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: w.descColor, lineHeight: 1.5 }}>
                  {w.desc}
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>

    </Box>
  );
};

export default UserDashboard; 